import React, { useEffect, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Loader2, RefreshCw, ExternalLink, CheckCircle2, XCircle, Star, MapPin, AlertCircle } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

export default function TripAdvisorPanel() {
  const { toast } = useToast();
  const [status, setStatus] = useState(null); // {connected, locationId, locationName, webUrl, reviewCountInDb}
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [pulling, setPulling] = useState(false);

  // Last pull log from the Maintenance page (section=monthly, item_key=m_tripadvisor_reviews)
  const logsQuery = useQuery({
    queryKey: ['maintenance-logs'],
    queryFn: async () => {
      const res = await base44.entities.MaintenanceLog.list('-created_date', 50);
      return Array.isArray(res) ? res : (res?.data || []);
    },
  });
  const logs = logsQuery.data || [];
  const taLogs = logs.filter((l) => l.item_key === 'm_tripadvisor_reviews' && l.section === 'monthly');
  const lastPull = taLogs[0];

  async function fetchStatus() {
    setLoadingStatus(true);
    try {
      const res = await base44.functions.invoke('tripadvisorReviewsPull', { statusOnly: true });
      const d = res?.data || res;
      if (d?.error) {
        setStatus({ connected: false, error: d.error });
      } else {
        setStatus(d);
      }
    } catch (e) {
      setStatus({ connected: false, error: e?.message || 'Could not reach TripAdvisor' });
    } finally {
      setLoadingStatus(false);
    }
  }

  useEffect(() => { fetchStatus(); }, []);

  async function pullNow() {
    setPulling(true);
    try {
      const res = await base44.functions.invoke('tripadvisorReviewsPull', {});
      const d = res?.data || res;
      if (d?.error) {
        toast({ title: 'Pull failed', description: d.error, variant: 'destructive' });
      } else {
        toast({ title: 'TripAdvisor reviews pulled', description: d.summary || `${d.pulled} new review(s).` });
        fetchStatus();
        logsQuery.refetch();
      }
    } catch (e) {
      toast({ title: 'Pull failed', description: e?.message || 'Try again', variant: 'destructive' });
    } finally {
      setPulling(false);
    }
  }

  if (loadingStatus) {
    return (
      <div className="flex items-center justify-center py-16 text-white/40">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Checking TripAdvisor connection…
      </div>
    );
  }

  const connected = status?.connected;
  const webUrl = status?.webUrl;

  return (
    <div className="space-y-6">
      {/* Connection status */}
      <div className={`border p-5 ${connected ? 'border-emerald-500/30 bg-emerald-500/[0.04]' : 'border-red-500/30 bg-red-500/[0.04]'}`}>
        <div className="flex items-start gap-3">
          {connected ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
          ) : (
            <XCircle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <p className={`font-bold text-sm ${connected ? 'text-emerald-300' : 'text-red-300'}`}>
              {connected ? 'TRIPADVISOR CONNECTED' : 'NOT CONNECTED'}
            </p>
            {status?.error ? (
              <p className="text-white/50 text-xs mt-1 leading-relaxed">{status.error}</p>
            ) : connected ? (
              <p className="text-white/50 text-xs mt-1 leading-relaxed">
                Location ID <span className="text-white font-mono">{status.locationId}</span>
                {status.locationName ? ` · ${status.locationName}` : ''}
              </p>
            ) : (
              <p className="text-white/50 text-xs mt-1 leading-relaxed">
                Set the TRIPADVISOR_API_KEY and TRIPADVISOR_LOCATION_ID secrets in Settings → Secrets.
              </p>
            )}
            <div className="flex flex-wrap gap-4 mt-3 text-xs">
              <div>
                <span className="text-white/30">REVIEWS IN DB</span>{' '}
                <span className="text-white font-bold">{status?.reviewCountInDb ?? 0}</span>
              </div>
              {lastPull && (
                <div>
                  <span className="text-white/30">LAST PULL</span>{' '}
                  <span className="text-white">{format(new Date(lastPull.created_date), 'dd/MM/yyyy HH:mm')}</span>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={pullNow}
            disabled={pulling}
            className="shrink-0 bg-primary hover:bg-primary/90 text-white font-bold text-[10px] tracking-wider px-4 py-2.5 transition-colors disabled:opacity-40 flex items-center gap-1.5"
          >
            {pulling ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} PULL REVIEWS NOW
          </button>
        </div>
      </div>

      {/* Public profile link */}
      {webUrl && (
        <div className="border border-white/10 bg-white/[0.02] p-5 flex items-center gap-3">
          <MapPin className="w-4 h-4 text-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-white/40 text-[10px] font-bold tracking-widest">PUBLIC TRIPADVISOR PROFILE</p>
            <a href={webUrl} target="_blank" rel="noreferrer" className="text-primary text-sm font-bold hover:text-primary/80 break-all flex items-center gap-1.5 mt-1">
              {webUrl} <ExternalLink className="w-3 h-3 shrink-0" />
            </a>
          </div>
        </div>
      )}

      {/* How it works */}
      <div className="border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center gap-2 mb-3">
          <Star className="w-4 h-4 text-primary" />
          <h3 className="text-white font-bold text-sm tracking-wide">HOW THIS WORKS</h3>
        </div>
        <ol className="space-y-3 text-xs text-white/60 leading-relaxed">
          <li><span className="text-primary font-bold">1.</span> Pull reviews with the button above (or leave the daily 3am automation to do it). New TripAdvisor reviews are stored as approved <span className="text-white">experiences</span> reviews and appear in the homepage reviews carousel alongside Google & Trustpilot.</li>
          <li><span className="text-primary font-bold">2.</span> The overall rating summary block renders on the homepage and Customer Service page from the pulled reviews.</li>
          <li><span className="text-primary font-bold">3.</span> A separate TripAdvisor-only review-request email is sent ~24 hours after each completed session, linking customers to your TripAdvisor profile to write a review. Shop orders are never emailed about TripAdvisor.</li>
        </ol>
      </div>

      {/* Pull history */}
      {taLogs.length > 0 && (
        <div className="border border-white/10 bg-white/[0.02] p-5">
          <h3 className="text-white font-bold text-sm tracking-wide mb-3">PULL HISTORY</h3>
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 bg-[#0d0d0f] border-b border-white/5">
                <tr className="text-white/30 font-bold tracking-wider">
                  <th className="px-2 py-2 w-6"></th>
                  <th className="px-2 py-2">RESULT</th>
                  <th className="px-2 py-2 text-right">WHEN</th>
                </tr>
              </thead>
              <tbody>
                {taLogs.map((l) => (
                  <tr key={l.id} className="border-b border-white/5 last:border-0">
                    <td className="px-2 py-2">
                      {l.status === 'run_success' ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                      ) : (
                        <XCircle className="w-3.5 h-3.5 text-red-400" />
                      )}
                    </td>
                    <td className="px-2 py-2 text-white/60">{l.summary || '—'}</td>
                    <td className="px-2 py-2 text-white/40 text-right whitespace-nowrap">{format(new Date(l.created_date), 'dd/MM/yy HH:mm')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!connected && (
        <div className="border border-amber-500/30 bg-amber-500/[0.04] p-4 flex items-start gap-3">
          <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
          <p className="text-amber-200/70 text-xs leading-relaxed">
            Add your TripAdvisor Content API key as <span className="font-mono text-amber-300">TRIPADVISOR_API_KEY</span> and your location ID as <span className="font-mono text-amber-300">TRIPADVISOR_LOCATION_ID</span> in Settings → Secrets, then refresh this panel.
          </p>
        </div>
      )}
    </div>
  );
}